/**
 * Draw image region to canvas and return JPEG blob (for react-easy-crop output).
 * croppedAreaPixels from react-easy-crop is in the source image's pixel space (natural size).
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (e) => reject(e));
    img.src = src;
  });
}

/**
 * @param {string} imageSrc - object URL or data URL
 * @param {{ x: number, y: number, width: number, height: number }} pixelCrop - natural pixels
 * @param {{
 *   outputWidth?: number,
 *   outputHeight?: number,
 *   maxWidth?: number,
 *   maxHeight?: number,
 * }} [opts] - If outputWidth & outputHeight are set, the file is exactly that size (banner: see bannerStripStyles).
 * @returns {Promise<Blob>}
 */
function clampCropToImage(image, crop) {
  const nw = image.naturalWidth;
  const nh = image.naturalHeight;
  let x = Math.round(crop.x);
  let y = Math.round(crop.y);
  let w = Math.round(crop.width);
  let h = Math.round(crop.height);
  x = Math.max(0, Math.min(x, Math.max(0, nw - 1)));
  y = Math.max(0, Math.min(y, Math.max(0, nh - 1)));
  w = Math.max(1, Math.min(w, nw - x));
  h = Math.max(1, Math.min(h, nh - y));
  return { x, y, width: w, height: h };
}

export async function getCroppedImageBlob(imageSrc, pixelCrop, opts = {}) {
  const { outputWidth, outputHeight, maxWidth = 1600, maxHeight = 400 } = opts;
  const image = await loadImage(imageSrc);
  const safe = clampCropToImage(image, pixelCrop);
  const sw = safe.width;
  const sh = safe.height;

  let outW;
  let outH;
  if (outputWidth != null && outputHeight != null) {
    outW = Math.round(outputWidth);
    outH = Math.round(outputHeight);
  } else {
    outW = sw;
    outH = sh;
    if (outW > maxWidth) {
      const r = maxWidth / outW;
      outW = maxWidth;
      outH = Math.round(outH * r);
    }
    if (outH > maxHeight) {
      const r = maxHeight / outH;
      outH = maxHeight;
      outW = Math.round(outW * r);
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(outW));
  canvas.height = Math.max(1, Math.round(outH));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  ctx.drawImage(
    image,
    safe.x,
    safe.y,
    safe.width,
    safe.height,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      },
      'image/jpeg',
      0.9
    );
  });
}
